import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import compose from 'lodash.flowright';
import { graphql } from 'react-apollo';
import { connect } from 'react-redux';

import {
  WATERMARK_PLACEMENTS,
  WATERMARK_PLACEMENTS_RATIOS,
} from '../../../../../../constants/watermark-placements';
import { photoUploadFilter } from '../../../../../../util/photo-upload';
import { getErrorString } from '../../../../../../util/normalize-errors';
import ImageUpload from '../../../../../../components/global/ImageUpload';
import { IMAGE_SIZE_CODE } from '../../../../../../constants/image-size-codes';
import CustomControl from '../../../../../../components/global/CustomControl';
import Alert from '../../../../../../components/global/Alert';
import { MeQuery } from '../../../../../../graphql/queries/account';
import { assetImagesUpload as assetImagesUploadMutation } from '../../../../../../graphql/mutations/account';
import { photoUploadSuccess as photoUploadSuccessAction } from '../../../../../../actions/photo-upload';
import useBasename from '../../../../../../components/routing/useBasename';
import { BASENAME } from '../../../../../../constants/basenames';

const propTypes = {
  userId: PropTypes.string,
  assetImagesUpload: PropTypes.func.isRequired,
  photoUploadSuccess: PropTypes.func.isRequired,
  handleChange: PropTypes.func,
  values: PropTypes.shape({
    watermark_config: PropTypes.shape({
      id: PropTypes.string,
      asset_image: PropTypes.shape({
        id: PropTypes.string,
        url: PropTypes.string,
      }),
      placement: PropTypes.oneOf(Object.values(WATERMARK_PLACEMENTS)),
    }),
  }),
  disabled: PropTypes.bool,
};

const AlbumFolderBranding = ({
  userId,
  assetImagesUpload,
  photoUploadSuccess,
  handleChange,
  values,
  disabled = false,
}) => {
  const basename = useBasename();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const hasBrandFilter = !!(
    values.watermark_config?.id || values.watermark_config?.asset_image?.id
  );

  const onBrandFilterChange = async event => {
    const [image] = event.target.files;

    if (!image) {
      return;
    }

    if (!photoUploadFilter(image)) {
      setError('JPG & PNG only');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const assetImagesUploadResponse = await assetImagesUpload(userId, [
        { filename: image.name, content_type: image.type },
      ]);
      const [assetImageUploadData] =
        assetImagesUploadResponse.data.assetImagesUpload.image_uploads;

      await axios.put(assetImageUploadData.url, image, {
        headers: { 'Content-Type': image.type },
      });

      photoUploadSuccess({ id: assetImageUploadData.id, file: image }, true);

      const reader = new FileReader();
      reader.onload = e => {
        handleChange({
          target: {
            name: 'watermark_config',
            value: {
              ...values.watermark_config,
              asset_image: {
                id: assetImageUploadData.id,
                url: e.target.result,
              },
              placement:
                values.watermark_config?.placement || WATERMARK_PLACEMENTS.SE,
              is_new: true,
            },
          },
        });
      };
      reader.readAsDataURL(image);
    } catch (err) {
      setError(getErrorString(err));
    } finally {
      setLoading(false);
    }
  };

  const onBrandFilterDismiss = () => {
    handleChange({
      target: {
        name: 'watermark_config',
        value: {},
      },
    });
  };

  const onBrandFilterPlacementChange = placement => {
    handleChange({
      target: {
        name: 'watermark_config',
        value: {
          ...values.watermark_config,
          placement,
        },
      },
    });
  };

  return (
    <Fragment>
      <div className="brand-filter-wrap">
        <div
          className={`brand-filter-upload-layout${
            hasBrandFilter ? '' : ' brand-filter-upload-layout-single'
          }`}
        >
          <div>
            <div className="brand-filter-section">
              <h3 className="brand-filter-section-title">
                UPLOAD YOUR BRAND FILTER
              </h3>
              <ImageUpload
                disabled={disabled}
                id="brand-filter-upload"
                className="mb-1"
                loading={loading}
                image={
                  values.watermark_config.asset_image?.id
                    ? {
                        ...values.watermark_config.asset_image,
                        thumbnail_urls: values.watermark_config.asset_image.url
                          ? [
                              {
                                size_code: IMAGE_SIZE_CODE.LARGE2X,
                                url: values.watermark_config.asset_image.url,
                                width: 1,
                                height: 1,
                              },
                            ]
                          : [],
                      }
                    : null
                }
                onInputChange={onBrandFilterChange}
                onImageDismiss={onBrandFilterDismiss}
                buttonText={`Upload your ${
                  [BASENAME.SHUTTERBUG, undefined].includes(basename)
                    ? 'custom photo overlay'
                    : 'brand filter'
                }`}
              />
            </div>

            {hasBrandFilter && !disabled && (
              <div className="brand-filter-placement-section">
                <h3 className="brand-filter-section-title">
                  SELECT BRAND FILTER PLACEMENT
                </h3>
                <div className="brand-filter-options">
                  <div className="brand-filter-option-card">
                    <CustomControl
                      id="brand-filter-corner"
                      type="radio"
                      name="brand-filter-placement"
                      label="Bottom right corner"
                      disabled={
                        !values.watermark_config?.id &&
                        !values.watermark_config?.asset_image?.id
                      }
                      checked={
                        values.watermark_config?.placement ===
                        WATERMARK_PLACEMENTS.SE
                      }
                      onChange={() => {
                        if (
                          values.watermark_config?.id ||
                          values.watermark_config?.asset_image?.id
                        ) {
                          onBrandFilterPlacementChange(WATERMARK_PLACEMENTS.SE);
                        }
                      }}
                    />
                    <div className="brand-filter-option-note">
                      Suggested file: 700px min. width, PNG
                    </div>
                  </div>

                  <div className="brand-filter-option-card">
                    <CustomControl
                      id="brand-filter-fullwidth"
                      type="radio"
                      name="brand-filter-placement"
                      label="Full bottom banner"
                      disabled={
                        !values.watermark_config?.id &&
                        !values.watermark_config?.asset_image?.id
                      }
                      checked={
                        values.watermark_config?.placement ===
                        WATERMARK_PLACEMENTS.SC
                      }
                      onChange={() => {
                        if (
                          values.watermark_config?.id ||
                          values.watermark_config?.asset_image?.id
                        ) {
                          onBrandFilterPlacementChange(WATERMARK_PLACEMENTS.SC);
                        }
                      }}
                    />
                    <div className="brand-filter-option-note">
                      Suggested file: 2560x120, PNG
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {hasBrandFilter && (
            <div className="brand-filter-preview-section">
              <h3 className="brand-filter-section-title">PREVIEW</h3>
              <div className="brand-filter-preview">
                <img
                  src="https://d3j43cz4mrk8gw.cloudfront.net/medium2x/98381bd8-05b4-5bb6-987a-416c47ff0faa.jpg"
                  alt="Preview"
                  className="brand-filter-preview-photo"
                />
                {values.watermark_config.asset_image?.url && (
                  <img
                    src={values.watermark_config.asset_image.url}
                    alt="Brand filter"
                    className={`brand-filter-preview-overlay ${
                      values.watermark_config?.placement ===
                      WATERMARK_PLACEMENTS.SE
                        ? 'brand-filter-preview-overlay-corner'
                        : 'brand-filter-preview-overlay-banner'
                    }`}
                    style={{
                      maxWidth:
                        WATERMARK_PLACEMENTS_RATIOS[
                          values?.watermark_config?.placement ??
                            WATERMARK_PLACEMENTS.SE
                        ],
                      maxHeight:
                        WATERMARK_PLACEMENTS_RATIOS[
                          values?.watermark_config?.placement ??
                            WATERMARK_PLACEMENTS.SE
                        ],
                      opacity: values.watermark_config?.transparency ?? 0.3,
                    }}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {error && <Alert type="danger">{error}</Alert>}
    </Fragment>
  );
};

AlbumFolderBranding.propTypes = propTypes;

const withUserData = graphql(MeQuery, {
  props: ({ data } = {}) => ({
    userId: data?.me?.id,
  }),
});

const withAssetImageUpload = graphql(assetImagesUploadMutation, {
  props: ({ mutate }) => ({
    assetImagesUpload: (accountId, images) =>
      mutate({
        variables: {
          input: {
            account_id: accountId,
            images,
          },
        },
      }),
  }),
});

const mapDispatchToProps = {
  photoUploadSuccess: photoUploadSuccessAction,
};

export default compose(
  withUserData,
  withAssetImageUpload,
  connect(null, mapDispatchToProps),
)(AlbumFolderBranding);
